import React, { useCallback, useEffect, useState } from "react";
import baseUrl from "../../api/baseUrl";
import getDecryptedToken from "../../api/getDecryptedToken";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../redux/loader/loaderActions";
import { useDispatch } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import {
  type LogEntry,
  type LogsResponse,
  type FilterForm,
  ACTION_OPTIONS,
  ENTITY_OPTIONS,
  getActionLabel,
  getEntityLabel,
  formatDetailsReadable,
  getReadableDescription,
  formatDetails,
  userName,
} from "./logsShared";

function MyLogs() {
  const dispatch = useDispatch();
  const currentUserId = getDecryptedToken("eid") || undefined;
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<LogsResponse["pagination"]>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  const methods = useForm<FilterForm>();
  const { watch } = methods;

  const fetchLogs = useCallback(
    (pageNum: number = 1, filters?: FilterForm) => {
      setLoading(true);
      setError(null);
      const params: Record<string, string | number> = {
        page: pageNum,
        limit: 50,
      };
      if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters?.dateTo) params.dateTo = filters.dateTo;
      if (filters?.action) params.action = filters.action;
      if (filters?.entity) params.entity = filters.entity;
      if (currentUserId) params.userId = currentUserId;

      baseUrl
        .get("log", { params })
        .then((response: { data: { success: boolean; data: LogEntry[]; pagination: LogsResponse["pagination"] } }) => {
          setLoading(false);
          dispatch(setloadeDeactive());
          if (response.data.success) {
            setLogs(response.data.data || []);
            setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
          }
        })
        .catch((err) => {
          setLoading(false);
          dispatch(setloadeDeactive());
          setError(err.response?.data?.message || "Failed to load logs");
        });
    },
    [dispatch, currentUserId]
  );

  useEffect(() => {
    dispatch(setloadeActive());
    fetchLogs(1, watch());
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only; watch in deps would refetch on every field change
  }, [fetchLogs, dispatch]);

  const handleFilter = useCallback(
    (values: FilterForm) => {
      fetchLogs(1, values);
    },
    [fetchLogs]
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      fetchLogs(newPage, watch());
    },
    [fetchLogs, watch]
  );

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-3 bg-gradient-to-r from-primary to-primaryLight border-b border-primary/20 flex items-center gap-2">
        <EventIcon className="text-white" />
        <h2 className="text-lg font-bold text-white">My logs</h2>
        <PersonIcon className="text-white/80" sx={{ fontSize: 20 }} />
      </div>

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleFilter)}
          className="p-4 border-b border-slate-100 dark:border-slate-700 flex flex-row flex-nowrap items-end gap-3 overflow-x-auto"
        >
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">From</label>
            <input
              type="date"
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm w-full min-w-[130px] bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              {...methods.register("dateFrom")}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">To</label>
            <input
              type="date"
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm w-full min-w-[130px] bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              {...methods.register("dateTo")}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Action</label>
            <select
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm min-w-[180px] bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              {...methods.register("action")}
            >
              <option value="">All actions</option>
              {ACTION_OPTIONS.map((a) => (
                <option key={a} value={a}>{getActionLabel(a)}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Entity</label>
            <select
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm min-w-[120px] bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              {...methods.register("entity")}
            >
              <option value="">All entities</option>
              {ENTITY_OPTIONS.map((e) => (
                <option key={e} value={e}>{getEntityLabel(e)}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-primary/20 transition-all shrink-0"
          >
            Apply
          </button>
        </form>
      </FormProvider>

      <div className="p-4">
        {error && (
          <div className="mb-3 py-2 px-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-lg">
            {error}
          </div>
        )}
        {loading ? (
          <div className="py-8 text-center text-slate-500 dark:text-slate-400 text-sm">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center text-slate-500 dark:text-slate-400 text-sm">No logs found.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary/5 dark:bg-slate-700/50 border-b border-primary/10 dark:border-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-primary dark:text-slate-200 uppercase w-[140px]">Time</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-primary dark:text-slate-200 uppercase w-[130px]" title="Staff who made this change">Who made this change</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-primary dark:text-slate-200 uppercase">What happened</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-primary dark:text-slate-200 uppercase w-[100px]">Action</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-primary dark:text-slate-200 uppercase w-[90px]">Entity</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-primary dark:text-slate-200 uppercase w-[140px]">Subject / details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {logs.map((entry) => (
                    <tr key={entry._id} className="hover:bg-primary/5 dark:hover:bg-slate-700/50">
                      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300 font-medium" title="Staff who made this change">
                        {userName(entry)}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-800 dark:text-slate-200" title={getReadableDescription(entry)}>
                        {getReadableDescription(entry)}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">{getActionLabel(entry.action || "")}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">{getEntityLabel(entry.entity || "")}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400 max-w-[220px]" title={formatDetailsReadable(entry.details)}>
                        {formatDetails(entry.details)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(logs.length > 0 || pagination.total > 0) && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 px-3 py-3 bg-primary/5 dark:bg-slate-700/50 rounded-lg border border-primary/10 dark:border-slate-600">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {pagination.total === 0 ? "0 logs" : (
                    <>Showing <span className="font-medium text-primary dark:text-slate-200">{(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-medium text-primary dark:text-slate-200">{pagination.total}</span> logs</>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <button type="button" disabled={pagination.page <= 1} onClick={() => onPageChange(1)} className="px-2.5 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed text-primary dark:text-slate-300 hover:bg-primary/10 dark:hover:bg-slate-600 border border-primary/20 dark:border-slate-600 transition-colors" title="First page">First</button>
                  <button type="button" disabled={pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)} className="px-2.5 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed text-primary dark:text-slate-300 hover:bg-primary/10 dark:hover:bg-slate-600 border border-primary/20 dark:border-slate-600 transition-colors">Previous</button>
                  <span className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400">Page <span className="font-semibold text-primary dark:text-slate-200">{pagination.page}</span> of <span className="font-semibold text-primary dark:text-slate-200">{pagination.totalPages || 1}</span></span>
                  <button type="button" disabled={pagination.page >= pagination.totalPages} onClick={() => onPageChange(pagination.page + 1)} className="px-2.5 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed text-primary dark:text-slate-300 hover:bg-primary/10 dark:hover:bg-slate-600 border border-primary/20 dark:border-slate-600 transition-colors">Next</button>
                  <button type="button" disabled={pagination.page >= pagination.totalPages} onClick={() => onPageChange(pagination.totalPages || 1)} className="px-2.5 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed text-primary dark:text-slate-300 hover:bg-primary/10 dark:hover:bg-slate-600 border border-primary/20 dark:border-slate-600 transition-colors" title="Last page">Last</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MyLogs;
